import { useMemo, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeftCircle,
  Calendar,
  CheckCircle2,
  FileText,
  Loader2,
  User,
  Award,
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { useAssignment, useAssignmentSubmissions } from '@/hooks/useAssignments'
import { useCourse } from '@/hooks/useCourses'
import { useUsersByIds } from '@/hooks/useUsers'
import { useAuth } from '@/contexts/AuthContext'
import { paths } from '@/routes/path'
import BackButton from '@/components/ui/BackButton'

const statusStyles: Record<string, string> = {
  submitted: 'bg-blue-100 text-blue-700',
  graded: 'bg-green-100 text-green-700',
  returned: 'bg-yellow-100 text-yellow-700',
  late: 'bg-red-100 text-red-700',
  draft: 'bg-gray-100 text-gray-600',
}

export function AssignmentSubmissionsPage() {
  const { assignmentId } = useParams<{ assignmentId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()

  const {
    data: assignment,
    isLoading: assignmentLoading,
    error: assignmentError,
  } = useAssignment(assignmentId ?? '')
  const {
    data: submissionsData,
    isLoading: submissionsLoading,
  } = useAssignmentSubmissions(assignmentId ?? '')

  const { data: course } = useCourse(assignment?.courseId)

  const submissions = submissionsData?.submissions ?? []
  const learnerIds = useMemo(
    () => submissions.map((submission) => submission.learnerId).filter(Boolean),
    [submissions]
  )

  const { data: learners, isLoading: learnersLoading } = useUsersByIds(learnerIds)

  if (!assignmentId) {
    return null
  }

  if (assignmentLoading || submissionsLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (assignmentError || !assignment) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Card className="max-w-md border-red-100 bg-red-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <FileText className="h-5 w-5" />
              Assignment unavailable
            </CardTitle>
            <CardDescription className="text-sm text-red-600">
              We could not load this assignment. Please return to the assignments list and try again.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  const totalSubmissions = submissions.length
  const gradedSubmissions = submissions.filter((submission) => submission.status === 'graded' || submission.grade).length
  const pendingSubmissions = totalSubmissions - gradedSubmissions
  const courseTitle = course?.title ?? assignment.courseId

  const handleBack = () => navigate(paths.app.assignments.detail.build(assignment.id))

  const handleViewSubmission = (submissionId: string) => {
    navigate(paths.app.assignments.submissions.detail.build(assignment.id, submissionId))
  }

  return (
    <div className="space-y-8">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-3">
          <div>
            {/* <p className="text-sm font-medium text-red-600">Assignment submissions</p> */}
            <div className='flex items-center gap-2'>
              <BackButton onClick={handleBack}/>
              <span className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                {courseTitle}
              </span>
            </div>
            <h1 className="mt-1 text-3xl font-bold text-gray-900">{assignment.title}</h1>
            <p className="mt-2 text-sm text-gray-600">
              Review learner submissions, track grading progress, and open individual workspaces to grade.
            </p>
          </div>
        </div>
        <div className="flex flex-col items-start gap-2 text-sm text-gray-600 sm:items-end">
          <span className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Due {new Date(assignment.dueDate).toLocaleString()}
          </span>
        </div>
      </header>

      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryCard
          title="Total submissions"
          value={totalSubmissions}
          icon={<FileText className="h-4 w-4" />}
        />
        <SummaryCard
          title="Graded"
          value={gradedSubmissions}
          icon={<CheckCircle2 className="h-4 w-4" />}
          tone="success"
        />
        <SummaryCard
          title="Pending review"
          value={pendingSubmissions}
          icon={<Loader2 className="h-4 w-4" />}
          tone="info"
        />
        <SummaryCard
          title="Max points"
          value={assignment.maxPoints}
          icon={<Award className="h-4 w-4" />}
        />
      </section>

      <section className="space-y-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Submissions</h2>
          {learnersLoading && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading learner details
            </div>
          )}
        </div>

        {totalSubmissions === 0 ? (
          <Card className="border-dashed">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-gray-600">
                <User className="h-4 w-4" />
                No submissions yet
              </CardTitle>
              <CardDescription>
                Learners haven&apos;t submitted this assignment yet. Check back closer to the due date.
              </CardDescription>
            </CardHeader>
          </Card>
        ) : (
          <div className="grid gap-4 lg:grid-cols-2">
            {submissions.map((submission) => {
              const learner = submission.learnerId ? learners?.[submission.learnerId] : undefined
              const learnerName = learner
                ? `${learner.firstName ?? ''} ${learner.lastName ?? ''}`.trim() || learner.email || 'Learner'
                : 'Learner'
              const submittedAt = submission.submittedAt
                ? new Date(submission.submittedAt).toLocaleString()
                : 'Not submitted'
              const gradePoints = submission.grade?.points
              const gradeMax = assignment.maxPoints ?? submission.grade?.maxPoints
              const gradeLabel =
                gradePoints !== undefined && gradePoints !== null
                  ? `${gradePoints}/${gradeMax ?? assignment.maxPoints}`
                  : 'Not graded'

              return (
                <Card key={submission.id} className="flex flex-col justify-between border border-gray-100 shadow-sm">
                  <CardHeader className="space-y-2">
                    <div className="flex flex-col gap-1">
                      <CardTitle className="text-lg font-semibold text-gray-900">{learnerName}</CardTitle>
                      {learner?.email && <p className="text-sm text-gray-500">{learner.email}</p>}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge className={statusStyles[submission.status] ?? 'bg-gray-100 text-gray-600'}>
                        {submission.status}
                      </Badge>
                      {submission.late && (
                        <Badge className="bg-red-100 text-red-700">Late</Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex flex-col gap-1 text-sm text-gray-600">
                      <span className="font-medium text-gray-800">Submitted</span>
                      <span>{submittedAt}</span>
                    </div>
                    <div className="flex flex-col gap-1 text-sm text-gray-600">
                      <span className="font-medium text-gray-800">Grade</span>
                      <span>{gradeLabel}</span>
                    </div>
                    <Button
                      onClick={() => handleViewSubmission(submission.id)}
                      className="w-full"
                      variant={submission.status === 'graded' ? 'secondary' : 'default'}
                    >
                      {submission.status === 'graded' ? 'View feedback' : 'Open for grading'}
                    </Button>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        )}
      </section>
    </div>
  )
}

function SummaryCard({
  title,
  value,
  icon,
  tone = 'default',
}: {
  title: string
  value: number
  icon: ReactNode
  tone?: 'default' | 'success' | 'info'
}) {
  const toneClasses: Record<string, string> = {
    default: 'bg-white',
    success: 'bg-green-50',
    info: 'bg-blue-50',
  }

  return (
    <Card className={toneClasses[tone]}>
      <CardContent className="flex items-center justify-between py-6">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{title}</p>
          <p className="mt-2 text-2xl font-semibold text-gray-900">{value}</p>
        </div>
        <div className="rounded-full bg-white p-3 text-gray-600 shadow-sm">{icon}</div>
      </CardContent>
    </Card>
  )
}

export default AssignmentSubmissionsPage
